import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
/* eslint-disable import/no-unresolved */
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  LineElement,
  PointElement
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import 'chartjs-adapter-dayjs-4/dist/chartjs-adapter-dayjs-4.esm';
/* eslint-enable import/no-unresolved */

import useLoadingStateClass from 'src/hooks/useLoadingStateClass';
import { sentenceCase, constantCase } from 'change-case';
import { useMarketPriceSeriesCollection } from 'src/Query/marketPrice';

ChartJS.register(
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  LineElement,
  PointElement
);

const lineChartOptions = {
  maintainAspectRatio: false,
  responsive: true,
  scales: {
    x: {
      type: 'time',
      ticks: {
        maxTicksLimit: 5
      },
      border: { display: false },
      grid: { display: false }
    },
    y: {
      position: 'right',
      ticks: {
        maxTicksLimit: 5
      },
      border: {
        dash: [2, 2]
      }
    }
  },
  elements: {
    line: {
      borderWidth: 1
    }
  },
  legend: {
    usePointStyle: true
  },
  plugins: {
    legend: {
      align: 'start',
      pointStyle: 'circle',
      position: 'bottom',
      reverse: false,
      labels: {
        color: '#323640',
        boxHeight: 6,
        boxWidth: 6,
        font: '10',
        padding: 15,
        pointStyle: 'circle',
        usePointStyle: true
      }
    }
  }
};

const dataSkeleton = {
  datasets: [
    {
      data: [
        { x: 0, y: 0 },
        { x: 1, y: 1 }
      ],
      fill: false,
      borderColor: '#eee',
      tension: 0.05
    }
  ]
};

const skeletonColor = '#e8e8e8';

const lineChartOptionsSkeleton = {
  // animations: false,
  maintainAspectRatio: false,
  responsive: true,
  scales: {
    x: {
      type: 'time',
      ticks: {
        maxTicksLimit: 5,
        display: false
      },
      grid: {
        display: false
      },
      border: {
        color: skeletonColor
      }
    },
    y: {
      ticks: {
        maxTicksLimit: 5,
        display: false
      },
      border: {
        color: skeletonColor
      }
    }
  },
  elements: {
    point: {
      pointStyle: false
    },
    line: {
      borderWidth: 1
    }
  },
  plugins: {
    legend: {
      display: false
    }
  }
};

const priceMid = (price) =>
  (price && (Number(price.high) + Number(price.low)) / 2) || undefined;

const lineSpecs = [
  {
    type: 'circularPcrResin',
    title: (source) => `Circular`,
    color: '#2F54EB',
    solid: true,
    borderWidth: 1.5
  },
  {
    type: 'pcrResin',
    title: (source) => `PCR Resin (${source})`,
    color: '#bb2E11',
    solid: true,
    borderWidth: 1
  },
  {
    type: 'virgin',
    title: (source) => `Virgin (${source})`,
    color: '#FA8C16',
    solid: false,
    borderWidth: 1
  },
  {
    type: 'flakes',
    title: (source) => `PCR Flakes (${source})`,
    color: '#389E0D',
    solid: false,
    borderWidth: 1
  },
  {
    type: 'bales',
    title: (source) => `Bales (${source})`,
    color: '#EB2F96',
    solid: false,
    borderWidth: 1
  },
  {
    type: 'tds',
    title: (source) => `Your price`,
    color: '#2F54EB',
    solid: false,
    borderWidth: 1
  },
  {
    type: 'cohort',
    title: (source) => `Supplier Cohort`,
    color: '#5CDBD3',
    solid: false,
    borderWidth: 1
  }
];

const formatSource = (name) =>
  name
    ? name.length <= 4
      ? constantCase(name)
      : sentenceCase(name)
    : 'Market';

const isSolid = (solid, defaultValue) => {
  if (solid === undefined) return defaultValue;
  return solid;
};

export default function MarketPriceByFormLineChart({
  filters,
  months,
  priceSettings = {}
}) {
  const { isLoading, data: seriesList } = useMarketPriceSeriesCollection(
    lineSpecs,
    filters,
    months,
    priceSettings
  );

  const loadingStateClass = useLoadingStateClass(isLoading);

  const data = useMemo(
    () => ({
      datasets: seriesList
        ?.map((series, index) => {
          if (series) {
            const solid = isSolid(
              priceSettings[series.type]?.solid,
              series.solid
            );
            return {
              data:
                series.data?.curve?.map((point) => ({
                  x: dayjs(point.timestamp).unix() * 1000,
                  y: priceMid(point)
                })) || [],
              label: series.title(
                formatSource(series?.data?.tickers[0]?.source)
              ),
              circularPriority: solid ? 'A' : 'B',
              fill: priceSettings[series.type]?.fill || false,
              borderColor: priceSettings[series.type]?.color || series.color,
              borderDash: solid ? undefined : [4, 3],
              borderWidth:
                priceSettings[series.type]?.width || series.borderWidth,
              tension: 0.05,
              elements: {
                point: {
                  // pointStyle: true,
                  backgroundColor: (ctx) =>
                    solid ? ctx?.element?.options?.borderColor : 'transparent',
                  pointRadius: (ctx) =>
                    series.data?.curve?.length &&
                    ctx.dataIndex === series.data.curve.length - 1
                      ? 2
                      : 0
                }
              }
            };
          }
          return [];
        })
        .filter((o) => o && o.data.length > 0)
        .sort((a, b) => a.circularPriority.localeCompare(b.circularPriority))
    }),
    [seriesList, months]
  );

  return (
    <div className={`market-price-chart__wrapper ${loadingStateClass}`}>
      {isLoading ? (
        <Line data={dataSkeleton} options={lineChartOptionsSkeleton} />
      ) : (
        <Line data={data} options={lineChartOptions} />
      )}
    </div>
  );
}

MarketPriceByFormLineChart.propTypes = {
  filters: PropTypes.object,
  months: PropTypes.number,
  priceSettings: PropTypes.object
};
